import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import LanguageSelector from "@/components/LanguageSelector";
import { useLanguage } from "@/i18n/LanguageContext";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/#storia", label: t('nav.myStory') },
    { href: "/#ecosistema", label: t('nav.ecosystem') },
    { href: "/pricing", label: t('nav.plans') },
    { href: "/area-paziente", label: t('nav.patientArea') },
  ];

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    if (href.startsWith("/#") && location.pathname === "/") {
      const element = document.getElementById(href.substring(2));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass border-b border-stone-200/50 shadow-[0_4px_30px_rgba(0,0,0,0.03)]"
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between py-4">
          <Link to="/" data-testid="logo-link">
            <Logo />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-stone-600 hover:text-[#2c4c3b] transition-colors text-sm font-medium tracking-wide"
                data-testid={`nav-link-${link.href}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/booking" data-testid="cta-prenota">
              <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-6 transition-all hover:-translate-y-0.5 hover:shadow-lg">
                {t('nav.book')}
              </Button>
            </Link>
            <LanguageSelector />
          </nav>

          {/* Mobile: Language + Menu */}
          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSelector />
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-stone-700" />
              ) : (
                <Menu className="w-6 h-6 text-stone-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav
            className="lg:hidden py-6 border-t border-stone-200/50 animate-fade-in"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-stone-600 hover:text-[#2c4c3b] transition-colors text-base font-medium py-2"
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full mt-2">
                  {t('nav.book')}
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
